import { useEffect, useMemo, useState } from 'react';
import { Copy } from 'lucide-react';
import { Modal } from './ui';
import { useShopping } from '../contexts/ShoppingContext';
import { CalendarCustomization, BusinessCardCustomization, CartItem, Product } from '../types';
import {
  PODProductType,
  CALENDAR_SLOTS,
  CALENDAR_LOCATION_DEFAULTS,
  getEligibleCopySources,
  getStableCartItemId,
  resolveFieldDefs,
  buildFieldMapping,
  computeCopyPreview,
  getLocationFieldValues,
  applyCopyToSlot,
  formatCopySourceLabel,
  OverwrittenField,
} from '../utils/copyPersonalization';

interface CopyPersonalizationButtonProps {
  targetProductType: PODProductType;
  targetProduct: Product;
  currentDestValue: CalendarCustomization | BusinessCardCustomization | undefined;
  excludeCartItemId?: string;
  onApply: (merged: CalendarCustomization | BusinessCardCustomization) => void;
}

type PendingCopy = {
  sourceLabel: string;
  overwritten: OverwrittenField[];
  merged: CalendarCustomization | BusinessCardCustomization;
  fieldCount: number;
};

export function CopyPersonalizationButton({
  targetProductType,
  targetProduct,
  currentDestValue,
  excludeCartItemId,
  onApply,
}: CopyPersonalizationButtonProps) {
  const { state } = useShopping();
  const [step, setStep] = useState<'closed' | 'picking' | 'confirming'>('closed');
  const [pending, setPending] = useState<PendingCopy | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const eligibleSources = useMemo(
    () => getEligibleCopySources(state.cart, targetProductType, excludeCartItemId),
    [state.cart, targetProductType, excludeCartItemId]
  );

  const destFields = useMemo(() => resolveFieldDefs(targetProduct, targetProductType), [targetProduct, targetProductType]);

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 2500);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const handleClose = () => {
    setStep('closed');
    setPending(null);
  };

  const buildCalendarCopy = (sourceItem: CartItem): PendingCopy | null => {
    const sourceFields = resolveFieldDefs(sourceItem.product, targetProductType);
    const mapping = buildFieldMapping(sourceFields, destFields);
    const currentLocations = (currentDestValue as CalendarCustomization | undefined)?.locations;
    const sourceLocations = sourceItem.calendarCustomization?.locations;
    if (!sourceLocations) return null;

    const mergedLocations: Record<string, Record<string, unknown>> = { ...currentLocations };
    let overwritten: OverwrittenField[] = [];
    let fieldCount = 0;

    for (const slot of CALENDAR_SLOTS) {
      const sourceLocation = sourceLocations[slot];
      if (!sourceLocation) continue;

      const sourceValues = getLocationFieldValues(sourceLocation, mapping.map(m => m.sourceKey));
      const destValues = getLocationFieldValues(currentLocations?.[slot], mapping.map(m => m.destKey));
      const preview = computeCopyPreview(sourceValues, destValues, mapping);
      if (Object.keys(preview.toWrite).length === 0) continue;

      const slotLabelPrefix = slot === 'primary' ? '' : `${slot.charAt(0).toUpperCase()}${slot.slice(1)}: `;
      overwritten = overwritten.concat(
        preview.overwritten.map(field => ({ ...field, label: `${slotLabelPrefix}${field.label}` }))
      );
      fieldCount += Object.keys(preview.toWrite).length;

      const base = currentLocations?.[slot] ?? CALENDAR_LOCATION_DEFAULTS;
      mergedLocations[slot] = applyCopyToSlot(base, preview.toWrite);
    }

    if (fieldCount === 0) return null;
    if (!mergedLocations.primary) mergedLocations.primary = { ...CALENDAR_LOCATION_DEFAULTS };

    return {
      sourceLabel: formatCopySourceLabel(sourceItem, targetProductType, sourceFields),
      overwritten,
      fieldCount,
      merged: { locations: mergedLocations } as unknown as CalendarCustomization,
    };
  };

  const buildFlatCopy = (sourceItem: CartItem): PendingCopy | null => {
    const sourceFields = resolveFieldDefs(sourceItem.product, targetProductType);
    const mapping = buildFieldMapping(sourceFields, destFields);
    const sourceData = targetProductType === 'business_card'
      ? sourceItem.businessCardCustomization
      : sourceItem.customizeItemCustomization;
    if (!sourceData) return null;

    const sourceValues = getLocationFieldValues(sourceData, mapping.map(m => m.sourceKey));
    const destValues = getLocationFieldValues(currentDestValue as BusinessCardCustomization | undefined, mapping.map(m => m.destKey));
    const preview = computeCopyPreview(sourceValues, destValues, mapping);
    if (Object.keys(preview.toWrite).length === 0) return null;

    return {
      sourceLabel: formatCopySourceLabel(sourceItem, targetProductType, sourceFields),
      overwritten: preview.overwritten,
      fieldCount: Object.keys(preview.toWrite).length,
      merged: applyCopyToSlot(currentDestValue as BusinessCardCustomization | undefined, preview.toWrite),
    };
  };

  const handlePickSource = (sourceItem: CartItem) => {
    const copy = targetProductType === 'calendar' ? buildCalendarCopy(sourceItem) : buildFlatCopy(sourceItem);
    if (!copy) {
      handleClose();
      return;
    }

    if (copy.overwritten.length === 0) {
      onApply(copy.merged);
      setSuccessMessage(`Copied ${copy.fieldCount} field${copy.fieldCount === 1 ? '' : 's'} from ${copy.sourceLabel}`);
      setStep('closed');
      return;
    }

    setPending(copy);
    setStep('confirming');
  };

  const handleConfirm = () => {
    if (!pending) return;
    onApply(pending.merged);
    handleClose();
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setStep('picking')}
          disabled={eligibleSources.length === 0}
          title={eligibleSources.length === 0 ? 'No other personalized items in cart yet' : undefined}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          <Copy className="w-4 h-4" />
          Copy Personalization
        </button>
        {successMessage && <span className="text-sm text-green-600">{successMessage}</span>}
      </div>

      <Modal isOpen={step === 'picking'} onClose={handleClose} title="Copy Personalization" size="sm">
        <p className="text-sm text-gray-600 mb-4">Choose an item already in your cart to copy matching fields from.</p>
        <div className="space-y-2">
          {eligibleSources.map(item => (
            <button
              key={getStableCartItemId(item)}
              type="button"
              onClick={() => handlePickSource(item)}
              className="w-full text-left px-4 py-3 text-sm border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
            >
              {formatCopySourceLabel(item, targetProductType, resolveFieldDefs(item.product, targetProductType))}
            </button>
          ))}
        </div>
      </Modal>

      <Modal isOpen={step === 'confirming'} onClose={handleClose} title="Overwrite existing values?" size="sm">
        {pending && (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Copying from {pending.sourceLabel} will overwrite {pending.overwritten.length} already-filled field{pending.overwritten.length === 1 ? '' : 's'}:
            </p>
            <div className="space-y-2 mb-6 max-h-64 overflow-y-auto">
              {pending.overwritten.map(field => (
                <div key={field.destKey} className="text-sm bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  <div className="font-medium text-gray-900">{field.label}</div>
                  <div className="text-gray-600">"{field.oldValue}" → "{field.newValue}"</div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
              >
                Overwrite and Copy
              </button>
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
